import {useLingui} from '@lingui/react/macro';
import {Svg, Path} from 'react-native-svg';
import {StyleSheet, View} from 'react-native';
import {useSelector, useDispatch} from 'react-redux';
import {useCallback, createRef, Fragment} from 'react';
import {useMenu} from 'extensions/viewport/useMenu';
import {usePopup} from 'extensions/viewport/usePopup';
import {IconButton} from 'interface/base/IconButton';
import {isTouch, isIOS} from 'utils/platform';
import {openPage, destroySession} from 'utils/mediafire';
import {colors, vectors} from 'config/styles';

import * as app from 'store/slices/app';

export function MenuLinks() {
  const {t} = useLingui();
  const isPremium = useSelector(app.selectors.isPremium);
  const email = useSelector(app.selectors.getEmail);
  const classes = {
    button: [
      styles.button,
      isTouch() && styles.buttonTouch,
    ]
  };

  const dispatch = useDispatch();
  const logout = useCallback(() => destroySession(dispatch), []);
  const gotoHelp = useCallback(() => openPage('help/submit_a_ticket.php'), []);
  const gotoDiscord = useCallback(() => openPage('https://discord.gg/wpNru5GaCe'), []);
  const gotoAccount = useCallback(() => openPage('myaccount/'), []);
  const gotoEarnSpace = useCallback(() => openPage('earnspace/'), []);

  const popup = usePopup();
  const menuRef = createRef<any>();
  const menuMoreLinks = useMenu(() =>
    [
      {
        command: 'header',
        text: t`More Links`,
      },
      {
        command: 'about',
        text: t`About`,
      },
      {
        command: 'policies',
        text: t`Policies`,
      },
      {
        command: 'privacy',
        text: t`Privacy`,
      },
      {
        command: 'abuse',
        text: t`Abuse`,
      },
      {
        command: 'help',
        text: t`Help`,
      },
    ],
    (command) => {
      switch (command) {
        case 'about':
          openPage('/about/');
          break;
        case 'policies':
          openPage('/policies/terms_of_service.php');
          break;
        case 'privacy':
          openPage('/policies/privacy_policy.php');
          break;
        case 'abuse':
          openPage('/policy_violation/terms_of_service.php');
          break;
        case 'help':
          openPage('/help/submit_a_ticket.php');
          break;
      }
      menuMoreLinks.exit();
    }
  , false);

  const showMoreMenu = () => {
    if (popup) {
      popup.setPosition(undefined);
      popup.setRef(menuRef);
    }
    menuMoreLinks.open();
  }

  return (
    <Fragment>
      {!isPremium && !isIOS() &&
        <View style={styles.item}>
          <IconButton
            onPress={gotoEarnSpace}
            title={t`Earn space`}
            text={t`Earn space`.toUpperCase()}
            textStyle={styles.text}
            buttonStyle={classes.button}
            buttonHoverStyle={styles.buttonHover}
            icon={
              <Svg viewBox="0 0 20 20" width={20} height={20}>
                <Path d={vectors.icons.plus} fill={colors.neutral.mid}/>
              </Svg>
            }
          />
        </View>
      }
      {email &&
        <View style={styles.item}>
          <IconButton
            onPress={gotoAccount}
            title={t`Open account settings`}
            text={t`Account`.toUpperCase()}
            textStyle={styles.text}
            buttonStyle={classes.button}
            buttonHoverStyle={styles.buttonHover}
            icon={
              <Svg viewBox="0 0 20 20" width={20} height={20}>
                <Path d={vectors.icons.gear} fill={colors.neutral.mid}/>
              </Svg>
            }
          />
        </View>
      }
      <View style={styles.item}>
        <IconButton
          onPress={logout}
          title={t`Logout`}
          text={t`Logout`.toUpperCase()}
          textStyle={styles.text}
          buttonStyle={classes.button}
          buttonHoverStyle={styles.buttonHover}
          icon={
            <Svg viewBox="0 0 20 20" width={20} height={20}>
              <Path d={vectors.icons.exit} fill={colors.neutral.mid} />
            </Svg>
          }/>
      </View>
      <View style={styles.item}>
        <IconButton
          onPress={gotoHelp}
          title={t`Open support ticket page`}
          text={t`Help`.toUpperCase()}
          textStyle={styles.text}
          buttonStyle={classes.button}
          buttonHoverStyle={styles.buttonHover}
          icon={
            <Svg viewBox="0 0 20 20" width={20} height={20}>
              <Path d={vectors.icons.help} fill={colors.neutral.mid} />
            </Svg>
          }/>
      </View>
      <View style={styles.item}>
        <IconButton
          onPress={gotoDiscord}
          title={t`Open discord invite`}
          text={'DISCORD'}
          textStyle={styles.text}
          buttonStyle={classes.button}
          buttonHoverStyle={styles.buttonHover}
          icon={
            <Svg viewBox="0 0 126.644 96" width={20} height={20}>
              <Path d={vectors.icons.discord} fill={colors.neutral.mid} />
            </Svg>
          }/>
      </View>
      <View style={styles.item} ref={menuRef}>
        <IconButton
          reference={menuMoreLinks.reference}
          onPress={showMoreMenu}
          title={t`More links`}
          text={t`More`.toUpperCase()}
          textStyle={styles.text}
          iconStyle={styles.iconMore}
          buttonStyle={classes.button}
          buttonHoverStyle={styles.buttonHover}
          icon={
            <Svg viewBox="0 0 20 20" width={20} height={20}>
              <Path
                d={vectors.icons.moreHoriz}
                fill={colors.neutral.mid}/>
            </Svg>
          }
        />
      </View>
    </Fragment>
  );
};

const styles = StyleSheet.create({
  iconMore: {
    marginTop: 16,
  },
  text: {
    color: colors.neutral.mid,
    fontSize: 12,
  },
  item: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    marginLeft: 11,
    marginRight: 8,
  },
  button: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignContent: 'flex-start',
    alignItems: 'center',
    paddingHorizontal: 10,
    lineHeight: 36,
    height: 36,
    width: 178,
    borderRadius: 0,
    color: colors.neutral.mid,
  },
  buttonHover: {
    backgroundColor: colors.neutral.darker,
  },
  buttonTouch: {
    lineHeight: 40,
    height: 40,
  },
});
